'use client';

import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import { ButtonWithLinkOrAction } from '@/components/buttons/Button.types';
import { useModalButtonActions } from '@/hooks/useModalButtonActions';
import { StyleOverride } from '@/types/craft-style-overrides';
import cn from 'classnames';
import Link from 'next/link';
import { FC, useState } from 'react';
import { useIntersectionObserverRef } from 'rooks';
import { BumperImage, BumperImageAnimatedPopover } from './DefaultImageElements';

export type PageBumperProps = {
  title: string;
  links: ButtonWithLinkOrAction[];
  backgroundColor?: string;
  staticImage?: React.ReactNode | null;
  styleOverrides?: StyleOverride;
}

export const PageBumper: FC<PageBumperProps> = ({ title, links = [], backgroundColor, staticImage, styleOverrides }) => {
  const [hasSeen, setHasSeen] = useState(false);
  const { modalActionHandler } = useModalButtonActions();

  // useIntersectionObserverRef and callback used to show BumperImageAnimatedPopover when element is in view
  const callback = (entries) => {
    let isIntersecting = false;

    if (entries && entries[0]) {
      isIntersecting = entries[0].isIntersecting;
    }
    if (isIntersecting) {
      setHasSeen(true);
    }
  };
  const [ref] = useIntersectionObserverRef(callback, { threshold: 0.1 });

  return (
    <SectionWrapper styleOverrides={styleOverrides} className="">
      <div ref={ref} className={cn(!styleOverrides ? 'py-4 md:py-12' : 'py-1', backgroundColor ? backgroundColor : "bg-white", 'px-[16px]')}>
        <div className="mx-auto max-w-grid">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-gutter-mobile md:gap-x-gutter-desktop">
            <div className="xl:col-span-7 bg-citron-500  rounded-[12px] md:max-h-[322px]">
              {/* CTA Section */}
              <div className="flex flex-wrap flex-col md:h-full md:justify-center p-3 gap-3 md:gap-2 lg:py-10 xl:py-0 lg:px-10">
                <p className="font-500 text-30/35 md:text-36/42 tracking-tight">{title}</p>
                <ul className="flex md:flex-row items-start md:items-center">
                  {links.map((link, index) => (
                    <li key={`bumper-link-${link.title?.replace(/\s/g, '-') ?? index}`}>
                      {link.href.length ? (
                        <Link
                          className={cn('block', {
                            'button button-size-medium button-primary-violet': link.type === 'primary',
                            'button button-size-medium button-secondary-violet': link.type === 'secondary',
                          })}
                          href={link.href}
                        >
                          {link.title}
                        </Link>
                      ) : (
                        <button
                          className={cn('block', {
                            'button button-size-medium button-primary-blue-900': link.type === 'primary',
                            'button button-size-medium button-secondary-blue-900 !ml-[12px]': link.type === 'secondary',
                          })}
                          onClick={() => {
                            modalActionHandler(link.action);
                          }}
                        >
                          {link.title}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              {/* Mobile Image Section */}
              <div className="px-1 pb-1 md:hidden">
                <div className="relative mt-[40px]">
                  <div className="inset-0 flex overflow-hidden rounded-10 mt-3">
                    {staticImage || <BumperImage />}
                  </div>
                  {!staticImage && (
                    <div className="absolute inset-0 flex flex-col justify-end items-center pb-[5%]">
                      <BumperImageAnimatedPopover hasSeen={hasSeen} className="px-[8%]" />
                    </div>)}
                </div>
              </div>
            </div>
            {/* Desktop Image Column */}
            <div className="relative hidden xl:block col-span-5 pointer-events-none ml-[-8px]">
              <div className="inset-0 overflow-hidden rounded-[12px]">
                {staticImage || <BumperImage additionalClasses="max-h-[322px]" />}
              </div>
              {!staticImage && (
                <div className="absolute inset-0 flex flex-col justify-end items-center pb-[15%]">
                  <BumperImageAnimatedPopover hasSeen={hasSeen} className="px-[15%]" />
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};
